import type { ClinicalSummaryReview } from '@/pages/order/clinicalSummaryReviewTypes'

export function clinicalSummaryDecisionToastMessage(label: string, categoryName: string): string {
  return `${label} added as ${categoryName}`
}

/** A chip already on the order, moved from its chip menu. */
export function clinicalSummaryMoveToastMessage(label: string, categoryName: string): string {
  return `${label} moved to ${categoryName}`
}

/** The orphan note's Remove all, which takes every term no longer in the summary off the order at once. */
export function clinicalSummaryOrphanRemovedToastMessage(count: number): string {
  return `${count === 1 ? '1 phenotype' : `${count} phenotypes`} removed from the order`
}

export function clinicalSummaryTrimToastMessage(count: number): string {
  return count === 1
    ? '1 phenotype is no longer in your summary and stays on the order until you remove it'
    : `${count} phenotypes are no longer in your summary and stay on the order until you remove them`
}

export interface ClinicalSummaryReopenCounts {
  addedByHand: number
  categoryChanged: number
  /** Codes the clinician removed, in review order. */
  removed: string[]
}

/** What a saved review records as the clinician's own work, for the reopen toast. */
export function clinicalSummaryReopenCounts(
  review: ClinicalSummaryReview,
  diagnosisCodes: ReadonlySet<string>
): ClinicalSummaryReopenCounts {
  let addedByHand = 0
  let categoryChanged = 0
  const removed: string[] = []
  for (const [code, term] of Object.entries(review.terms)) {
    if (term.reviewState === 'removed') {
      removed.push(code)
    } else if (term.reviewState === 'active' && diagnosisCodes.has(code)) {
      if (term.origin === 'clinician') addedByHand += 1
      else if (term.categoryOwner === 'clinician') categoryChanged += 1
    }
  }
  for (const code of review.tombstones) {
    if (!removed.includes(code) && !diagnosisCodes.has(code)) removed.push(code)
  }
  return { addedByHand, categoryChanged, removed }
}

/** `null` when the saved review holds nothing the clinician did. */
export function clinicalSummaryReopenToastMessage({
  addedByHand,
  categoryChanged,
  removed,
}: ClinicalSummaryReopenCounts): string | null {
  const clauses = [
    addedByHand > 0 ? `${addedByHand} phenotype${addedByHand === 1 ? '' : 's'} added by hand` : null,
    categoryChanged > 0 ? `${categoryChanged} ${categoryChanged === 1 ? 'category' : 'categories'} changed` : null,
    removed.length > 0 ? `${removed.length} removed` : null,
  ].filter((clause): clause is string => clause !== null)
  return clauses.length > 0 ? `Saved order loaded: ${clauses.join(', ')}` : null
}
